import jwt from '../../http/requests/auth/jwt/index.js'
import twofa from '../../http/requests/auth/twofa.js'

import router from '@/router'
import {
  rolesString, landingPathFor, isPathAllowedForRoles, TWOFA_SETUP_FLAG, setReadOnlyUser
} from '@/helpers/roleAccess.js'

// Older builds returned `language` as an unawaited Sequelize promise (the
// frontend then read `language.fulfillmentValue.code`). The new buildAuthPayload
// in auth.controller.js awaits it, so `language.code` is now the canonical path.
function languageCode (lang) {
  if (!lang) return null
  if (lang.code) return lang.code
  if (lang.fulfillmentValue && lang.fulfillmentValue.code) return lang.fulfillmentValue.code
  return null
}

function persistAuthSession (data, opts) {
  opts = opts || {}
  localStorage.setItem('accessToken', data.accessToken)
  localStorage.setItem('login-id', data.id)
  localStorage.setItem('login-username', data.username)
  localStorage.setItem('login-avatar', data.avatarsrc)
  localStorage.setItem('login-email', data.email)
  localStorage.setItem('login-roles', data.roles || [])
  const code = languageCode(data.language)
  if (code) localStorage.setItem('login-language', code)
  localStorage.setItem('login-broker', data.idbroker)
  localStorage.setItem('login-date', new Date())
  // Compte en lecture seule (Broker Status) : bandeau + boutons dès le premier rendu.
  setReadOnlyUser(data.read_only === true)

  if (opts.trustedDeviceToken) {
    localStorage.setItem('xylo:trusted-device-token', opts.trustedDeviceToken)
  }
  if (opts.mustEnable2fa) {
    localStorage.setItem('xylo:must-enable-2fa', '1')
  } else {
    localStorage.removeItem('xylo:must-enable-2fa')
  }
  // Cache 2FA status from the login response so the global modal can decide
  // whether to render before any /2fa/status round-trip completes.
  if (typeof data.twofa_enabled === 'boolean') {
    try {
      localStorage.setItem('xylo:twofa-status', JSON.stringify({
        twofa_enabled: data.twofa_enabled,
        twofa_method: data.twofa_method || null
      }))
    } catch (e) { /* ignore */ }
  }
}

// Page d'atterrissage par rôle : helpers/roleAccess.js (une seule source, aussi
// utilisée par le garde du routeur et les redirections des tableaux de bord).
function routeAfterLogin () {
  const roles = rolesString()
  const to = router.currentRoute.query.to
  const home = landingPathFor(roles)
  // Un lien profond (?to=) est honoré, sauf s'il sort du périmètre de la comptable ou du client.
  const target = (to && isPathAllowedForRoles(to, roles)) ? to : home
  router.push(target).catch(() => {})
}

/* eslint-disable no-unused-vars */
export default {
  loginJWT ({ commit }, payload) {
    return new Promise((resolve, reject) => {
      jwt.login(payload.userDetails.email, payload.userDetails.password)
        .then(response => {
          const data = (response && response.data) || {}

          // Path 1 — 2FA enabled, no trusted device. Don't persist anything;
          // the login view will pivot to the challenge component.
          if (data.requires2fa) {
            return resolve({
              requires2fa: true,
              challengeToken: data.challengeToken,
              method: data.method
            })
          }

          // Path 1b — role with mandatory 2FA and none configured yet: the
          // server returned a setup-only token (scope pre-2fa-setup, 15 min)
          // instead of a session. Keep only what the dedicated full page
          // needs and go there; the router guard pins every other path to it.
          if (data.twofaRequired) {
            localStorage.setItem('accessToken', data.setupToken)
            localStorage.setItem('login-email', data.email || payload.userDetails.email)
            if (data.username) localStorage.setItem('login-username', data.username)
            localStorage.setItem(TWOFA_SETUP_FLAG, '1')
            router.push('/pages/2fa-setup').catch(() => {})
            return resolve({ twofaRequired: true })
          }

          // Path 2 — user must enable 2FA. Scoped JWT only allows /2fa/setup/*.
          // The post-login modal blocks the rest of the UI until activation.
          if (data.mustEnable2fa) {
            persistAuthSession(data, { mustEnable2fa: true })
            routeAfterLogin()
            return resolve({ mustEnable2fa: true })
          }

          // Path 3 — normal success. Preserve legacy "Welcome back!" toast
          // by rejecting with the OK sentinel before completing the resolve.
          if (data.message === 'OK') {
            persistAuthSession(data)
            routeAfterLogin()
            // eslint-disable-next-line prefer-promise-reject-errors
            reject({ message: 'OK' })
            return resolve(response)
          }

          return reject({ message: data.message || 'Login failed' })
        })
        .catch(error => reject(error))
    })
  },

  // Step 2 of login when 2FA is enabled.
  submit2faChallenge ({ commit }, { challengeToken, code, useRecovery, rememberDevice }) {
    return new Promise((resolve, reject) => {
      twofa.signin2fa({ challengeToken, code, useRecovery, rememberDevice })
        .then(response => {
          const data = (response && response.data) || {}
          if (data.message !== 'OK') {
            return reject({ message: data.message || '2FA verification failed' })
          }
          persistAuthSession(data, { trustedDeviceToken: data.trustedDeviceToken })
          routeAfterLogin()
          resolve(response)
        })
        .catch(error => reject(error))
    })
  },

  // End of the forced 2FA setup (/pages/2fa-setup): the activation response
  // is the full auth payload (+ recovery codes) → open a normal session.
  completeTwofaSetup ({ commit }, payload) {
    persistAuthSession(payload)
    localStorage.removeItem(TWOFA_SETUP_FLAG)
    localStorage.removeItem('xylo:must-enable-2fa')
    routeAfterLogin()
  },

  // Refresh the cached 2FA status — used on app boot and after the user
  // completes the mandatory activation flow.
  refreshTwofaStatus () {
    return twofa.status().then(res => {
      try { localStorage.setItem('xylo:twofa-status', JSON.stringify(res.data)) } catch (e) { /* ignore */ }
      // If the user just activated 2FA, the must-enable flag becomes obsolete.
      if (res.data && res.data.twofa_enabled) localStorage.removeItem('xylo:must-enable-2fa')
      return res.data
    })
  },

  registerUserJWT ({ commit }, payload) {
    const { displayName, email, password, confirmPassword } = payload.userDetails
    return new Promise((resolve, reject) => {
      if (password !== confirmPassword) {
        reject({ message: 'Password doesn\'t match. Please try again.' })
      }
      jwt.registerUser(displayName, email, password)
        .then(response => {
          if (response.data.message === 'OK') {
            router.push(router.currentRoute.query.to || '/pages/login')
          }
          reject({ message: response.data.message })
          resolve(response)
        })
        .catch(error => reject(error))
    })
  }
}
